/**
 * Funzioni di lookup per ottenere i bonus per CAP dal database JSON
 * Versione minimale per bonus_summary_data
 */

// Import del database JSON
import capBonusData from '../../data/capBonusData.json';

// Interfacce per i tipi di bonus
export interface CapBonusPV {
  ecobonus: number | null;
  pnrrCer: number | null;
  pnrrScadenza: string | null;
  bonusRegionale: number | null;
  bonusProvinciale: number | null;
  risparmioBolletta: number | null;
  rivenditaEnergia: number | null;
  rivenditaSurplus: number | null;
}

export interface CapBonusHP {
  contoTermico: number | null;
  ecobonus: number | null;
  bonusRegionale: number | null;
  bonusProvinciale: number | null;
  risparmioBolletta: number | null;
}

interface CapBonusEntry {
  cap: string;
  comune: string;
  provincia: string;
  regione: string;
  abitanti: number;
  abitanti50k: boolean;
  abitanti50k100k: boolean;
  bonusCountPV: number;
  bonusCountHP: number;
  bonusPV: CapBonusPV;
  bonusHP: CapBonusHP;
}

interface BonusLabels {
  [key: string]: string;
}

interface CapBonusDataWithLabels {
  _labels?: BonusLabels;
  [cap: string]: CapBonusEntry | BonusLabels | undefined;
}

// Cast del database al tipo corretto
const rawData = capBonusData as unknown as CapBonusDataWithLabels;

// Crea il database CAP escludendo _labels
const CAP_DATABASE: Record<string, CapBonusEntry> = Object.fromEntries(
  Object.entries(rawData).filter(([key]) => key !== '_labels')
) as Record<string, CapBonusEntry>;

/**
 * Valori di fallback per CAP non trovati nel database
 */
const FALLBACK_BONUS_PV: CapBonusPV = {
  ecobonus: 9899,
  pnrrCer: 5400,
  pnrrScadenza: '22.11.2026',
  bonusRegionale: null,
  bonusProvinciale: null,
  risparmioBolletta: 23800,
  rivenditaEnergia: 3430,
  rivenditaSurplus: 4189,
};

const FALLBACK_BONUS_HP: CapBonusHP = {
  contoTermico: 4657,
  ecobonus: 7160,
  bonusRegionale: null,
  bonusProvinciale: null,
  risparmioBolletta: 14400,
};

/**
 * Ottiene i dati bonus per un CAP specifico
 */
function getCapData(cap: string | undefined): CapBonusEntry | null {
  if (!cap || cap.length !== 5) return null;
  return CAP_DATABASE[cap] || null;
}

/**
 * Ottiene i bonus PV per un CAP, con fallback se non trovato
 */
export function getBonusPVForCap(cap: string | undefined): CapBonusPV {
  const data = getCapData(cap);
  return data?.bonusPV || FALLBACK_BONUS_PV;
}

/**
 * Ottiene i bonus HP per un CAP, con fallback se non trovato
 */
export function getBonusHPForCap(cap: string | undefined): CapBonusHP {
  const data = getCapData(cap);
  return data?.bonusHP || FALLBACK_BONUS_HP;
}
